import { checkConnection, isOffline } from "@/services/connectivity";

/**
 * App-wide chrome: theme, connectivity, and the blocking overlays.
 *
 * Deliberately small. Anything only one screen cares about (a phase, a disabled button)
 * belongs in that component's own `data`, not here.
 */
export default {
  namespaced: true,

  state: () => ({
    /** "" = follow the OS; otherwise "light" | "dark". */
    theme: "",
    /** Ambient banner: the device itself reports no network. */
    offline: isOffline(),
    /** Blocking dialog: null | "offline" | "server". */
    netError: null,
    /** True while a reachability probe is in flight. */
    checkingConnection: false,
    /** Global blocking spinner — only for genuinely app-blocking waits. */
    busy: false,
    /**
     * Camera + microphone have been granted at least once this session. Both media steps
     * request the same constraints, so the second one must not prime for a prompt the
     * browser will no longer show.
     */
    mediaGranted: false,
  }),

  mutations: {
    SET_THEME(state, theme) {
      state.theme = theme || "";
    },
    SET_OFFLINE(state, offline) {
      state.offline = !!offline;
    },
    SET_NET_ERROR(state, kind) {
      state.netError = kind || null;
    },
    SET_CHECKING(state, checking) {
      state.checkingConnection = !!checking;
    },
    SET_BUSY(state, busy) {
      state.busy = !!busy;
    },
    SET_MEDIA_GRANTED(state, granted) {
      state.mediaGranted = !!granted;
    },
  },

  actions: {
    /** Reflect a browser online/offline event into the ambient banner. */
    refreshOnline({ commit, state, dispatch }) {
      const offline = isOffline();
      commit("SET_OFFLINE", offline);
      // Coming back online while the offline dialog is up should re-probe rather than
      // make the user tap "retry" for something we already know changed.
      if (!offline && state.netError === "offline") dispatch("revalidate");
    },

    /**
     * Probe connectivity and update the dialog.
     * @returns {Promise<"ok"|"offline"|"server">}
     */
    async revalidate({ commit, state }) {
      if (state.checkingConnection) return state.netError || "ok";
      commit("SET_CHECKING", true);
      try {
        const kind = await checkConnection();
        commit("SET_NET_ERROR", kind === "ok" ? null : kind);
        commit("SET_OFFLINE", kind === "offline");
        return kind;
      } finally {
        commit("SET_CHECKING", false);
      }
    },

    toggleTheme({ commit, state }) {
      const root = document.documentElement;
      const current =
        state.theme ||
        (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light");
      const next = current === "dark" ? "light" : "dark";
      root.setAttribute("data-theme", next);
      commit("SET_THEME", next);
    },
  },
};
